/**
 * Seedream family on fal.ai (Phase 1, U8): seedream-4.5, seedream-5-lite (ByteDance).
 *
 * Replaces: falai.ts async image arms `case 'seedream-4.5'` / `case 'seedream-5-lite'`, the
 * generic builder in ProviderService.generateImageFalAI, and the FAL_IMAGE_MODELS /
 * FAL_IMAGE_TO_IMAGE_MODELS rows. Pricing: CreditService's generic tail (base x legacy ladder).
 * Namespace is `fal-ai/bytedance/...` (verified 200 against the oracle, 2026-09-19).
 *
 * PURE: `import type` + sibling modules only (purity.test).
 *
 * Legacy quirks reproduced on purpose:
 *   - editor adds max_images: 1; headless adds output_format 'png' and negative_prompt, neither
 *     of which exists in the Seedream schemas (dead keys, headless-only legacy.wire).
 *   - a maskUrl-only editor request picks the edit endpoint with NO image_urls (edit port min 0).
 *   - headless sends image_urls whenever inputs exist.
 */
import type { ModelDescriptor, Port, Sku } from '../types';
import { FAL_IMAGE_SIZE_PRESETS } from '../_shared';

const REFS_PORT: Port = { slot: 'refs', kind: 'image', min: 0, max: 10, handle: 'input-image' };
const REFS_WIRE = { field: 'image_urls', many: true, encode: 'image' } as const;

const seedream = (o: { id: 'seedream-4.5' | 'seedream-5-lite'; name: string; slug: string; sku: Sku }): ModelDescriptor => ({
  id: o.id, provider: 'falai', face: 'image', displayName: o.name, ledgerName: o.name, status: 'live',
  prompt: { field: 'prompt', required: false },
  modes: {
    t2i: { endpoint: `${o.slug}/text-to-image`, ports: [], media: {} },   // with inputs both surfaces pick edit
    edit: { endpoint: `${o.slug}/edit`, ports: [REFS_PORT], media: { refs: REFS_WIRE } },
  },
  fixed: { num_images: 1, enable_safety_checker: false },
  params: [
    { id: 'aspectRatio', type: 'string', default: '1:1', invalid: 'passthrough',
      wire: { kind: 'imageSize', field: 'image_size', presets: FAL_IMAGE_SIZE_PRESETS, fallback: 'square_hd' },
      ui: { label: 'Aspect ratio', tier: 'basic' } },
    { id: 'negativePrompt', type: 'string', default: null, invalid: 'passthrough', wire: false },
    { id: 'resolution', type: 'string', default: null, role: 'tier', invalid: 'passthrough', wire: false },
  ],
  legacy: {
    wire: [
      { field: 'max_images', value: 1, surface: 'editor', why: 'editor constant (equals the fal default 1)', retire: 'inert-cleanup' },
      { field: 'output_format', value: 'png', surface: 'headless',
        why: 'headless generic builder; absent from the Seedream schemas (oracle 2026-09-19)', retire: 'inert-cleanup' },
      { field: 'negative_prompt', fromParam: 'negativePrompt', surface: 'headless',
        why: 'headless generic builder when truthy; absent from the Seedream schemas', retire: 'inert-cleanup' },
    ],
  },
  pricing: { unit: 'generation', units: { fixed: 1 }, rounding: 'generic', policy: 'fal-image-35', skus: [o.sku] },
  schemaVerified: '2026-09-19',
});

export const SEEDREAM_45 = seedream({
  id: 'seedream-4.5', name: 'Seedream 4.5', slug: 'fal-ai/bytedance/seedream/v4.5',
  sku: {
    id: 'seedream-4.5',
    rate: { ladder: { base: 18, by: 'resolution', curve: 'legacy-resolution-v1' } },
    vendor: { usd: 0.04, per: 'image', field: 'sidebar', source: 'fal-ai/bytedance/seedream/v4.5/text-to-image and /edit (rendered "Your request will cost")',
      text: 'Your request will cost $0.04 per image.', verified: '2026-09-19' },
    marginException: '18 = ceil(0.04 x 450) (policy ceil(0.04 x 300/0.65) = 19). Kept (P1 price-neutral).',
    registry: { displayName: 'Seedream 4.5', operationType: 'image', flatRate: false },
  },
});

export const SEEDREAM_5_LITE = seedream({
  id: 'seedream-5-lite', name: 'Seedream 5 Lite', slug: 'fal-ai/bytedance/seedream/v5/lite',
  sku: {
    id: 'seedream-5-lite',
    rate: { ladder: { base: 18, by: 'resolution', curve: 'legacy-resolution-v1' } },
    vendor: { usd: 0.035, per: 'image', field: 'sidebar', source: 'fal-ai/bytedance/seedream/v5/lite/text-to-image and /edit (rendered "Your request will cost")',
      text: 'Your request will cost $0.035 per image.', verified: '2026-09-19' },
    marginException: '18 is ABOVE policy ceil(0.035 x 300/0.65) = 17 (live row). Kept (P1 price-neutral); owner decision.',
    registry: { displayName: 'Seedream 5 Lite', operationType: 'image', flatRate: false },
  },
});

export const SEEDREAM_MODELS: readonly ModelDescriptor[] = [SEEDREAM_45, SEEDREAM_5_LITE];
